import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { UploadCloud, Film, AlertCircle, X } from "lucide-react";

export default function UploadPage() {
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [adBudget, setAdBudget] = useState("");
    const [adFee, setAdFee] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [progress, setProgress] = useState(0);

    const onPickFile = (f) => {
        if (!f) return;
        if (!f.type.startsWith("video/")) {
            setError("Please select a video file");
            return;
        }
        if (f.size > 200 * 1024 * 1024) {
            setError("Video must be under 200MB");
            return;
        }
        setError("");
        setFile(f);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!file) return setError("Please select a video file");
        if (!title.trim()) return setError("Please enter a title");
        const form = new FormData();
        form.append("title", title);
        form.append("description", description);
        form.append("ad_budget", adBudget);
        form.append("ad_fee", adFee);
        form.append("file", file);
        setSubmitting(true);
        try {
            const { data } = await api.post("/videos", form, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (evt) => {
                    if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
                },
            });
            navigate(`/video/${data.id}`);
        } catch (e2) {
            setError(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter text-ink mb-2">
                Drop a new video
            </h1>
            <p className="font-body text-ink/70 mb-8">Upload a clip, set the ad budget &amp; fee, hit go.</p>

            <form onSubmit={onSubmit} className="space-y-6" data-testid="upload-form">
                {/* Dropzone */}
                <div
                    data-testid="upload-dropzone"
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        onPickFile(e.dataTransfer.files?.[0]);
                    }}
                    onClick={() => inputRef.current?.click()}
                    className={`cursor-pointer border-[4px] border-dashed border-ink rounded-2xl p-10 lg:p-16 text-center transition-all ${dragOver ? "bg-vibrantyellow" : file ? "bg-cream" : "bg-white"} hover:bg-vibrantyellow`}
                >
                    <input
                        ref={inputRef}
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={(e) => onPickFile(e.target.files?.[0])}
                        data-testid="upload-file-input"
                    />
                    {file ? (
                        <div className="flex items-center justify-center gap-3">
                            <Film className="w-8 h-8 text-vibrantorange" strokeWidth={2.5} />
                            <div className="text-left">
                                <div className="font-heading font-bold text-ink text-lg">{file.name}</div>
                                <div className="font-body text-sm text-ink/60">{(file.size / 1024 / 1024).toFixed(1)} MB</div>
                            </div>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setFile(null); }}
                                className="ml-4 w-9 h-9 bg-white border-[3px] border-ink rounded-lg flex items-center justify-center shadow-brutal hover:-translate-y-0.5 transition-transform"
                                data-testid="upload-clear-file"
                            >
                                <X className="w-4 h-4 text-ink" strokeWidth={3} />
                            </button>
                        </div>
                    ) : (
                        <>
                            <UploadCloud className="w-14 h-14 mx-auto text-ink mb-3" strokeWidth={2.5} />
                            <div className="font-heading font-black text-2xl text-ink">Drop your video here</div>
                            <div className="font-body text-ink/70 mt-2">or click to choose · MP4 / WebM · up to 200MB</div>
                        </>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="font-heading font-bold text-xs uppercase tracking-widest text-ink mb-2 block">Title *</label>
                        <input
                            data-testid="upload-title-input"
                            className="brutal-input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Give it a name"
                            maxLength={200}
                            required
                        />
                    </div>
                    <div>
                        <label className="font-heading font-bold text-xs uppercase tracking-widest text-ink mb-2 block">Description</label>
                        <input
                            data-testid="upload-description-input"
                            className="brutal-input"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="What's it about?"
                            maxLength={500}
                        />
                    </div>
                    <div>
                        <label className="font-heading font-bold text-xs uppercase tracking-widest text-ink mb-2 block">Video Uploader Budget</label>
                        <input
                            data-testid="upload-budget-input"
                            className="brutal-input"
                            value={adBudget}
                            onChange={(e) => setAdBudget(e.target.value)}
                            placeholder="e.g. $500"
                            maxLength={100}
                        />
                    </div>
                    <div>
                        <label className="font-heading font-bold text-xs uppercase tracking-widest text-ink mb-2 block">Payment for Advertising</label>
                        <input
                            data-testid="upload-fee-input"
                            className="brutal-input"
                            value={adFee}
                            onChange={(e) => setAdFee(e.target.value)}
                            placeholder="e.g. $50 / spot"
                            maxLength={100}
                        />
                    </div>
                </div>

                {error && (
                    <div data-testid="upload-error" className="flex items-start gap-2 bg-vibrantpink/15 border-[3px] border-vibrantpink text-ink rounded-xl p-3">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-vibrantpink" strokeWidth={2.5} />
                        <span className="font-body font-semibold text-sm">{error}</span>
                    </div>
                )}

                {submitting && progress > 0 && (
                    <div data-testid="upload-progress" className="brutal-card p-4 bg-cream">
                        <div className="flex items-center justify-between mb-2">
                            <span className="font-heading font-bold text-sm uppercase tracking-widest text-ink">Uploading</span>
                            <span className="font-heading font-black text-ink">{progress}%</span>
                        </div>
                        <div className="w-full h-3 bg-white border-[2px] border-ink rounded-full overflow-hidden">
                            <div
                                className="h-full bg-vibrantorange transition-all"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>
                )}

                <button
                    type="submit"
                    disabled={submitting}
                    className="brutal-btn-primary w-full text-lg py-4"
                    data-testid="upload-submit-button"
                >
                    {submitting ? "Uploading..." : "Publish Video"}
                </button>
            </form>
        </div>
    );
}
